/**
 * @file This file contains the user API functions.
 * @module api/userApi
 */

import { ENV } from '../../config/runtimeEnv';

import { User } from "../../types/admin/UserTypes";
import { withNormalisedDateOfBirth } from "../../utils/healthcare/ValidateDateOfBirth";
import { updateSessionUser } from "../../utils/auth/sessionUser";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

// auth's user-administration and profile routes moved to healthcare at the same paths (contract §15).
const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;



//Admin user management routes
// CU-86d438bbx: `userTypes` is the server-owned vocabulary; narrowed defensively so an
// older backend that omits the block yields [] rather than a broken Select.
export const fetchUsers = async (
	page: number,
	perPage: number,
	search?: string,
	role?: string,
	userType?: string
): Promise<{
	data: User[];
	total: number;
	userTypes: string[];
}> => {
	const response = await http.get(`${API_BASE_URL}/admin/users`, {
		params: {
			page,
			per_page: perPage,
			...(search ? { search } : {}),
			...(role ? { role } : {}),
			...(userType ? { user_type: userType } : {}),
		},
	});

	const offered = response.data?.filters?.user_types;

	return {
		data: response.data.data,
		total: response.data.pagination.total_items,
		userTypes: Array.isArray(offered)
			? offered.filter((type: unknown): type is string => typeof type === "string" && type !== "")
			: [],
	};
};

export const fetchSingleUserByUuid = async (
	uuid: string
): Promise<{
	data: User;
}> => {
	const response = await http.get(`${API_BASE_URL}/admin/usersSimpleUuid/${uuid}`);
	return {
		data: response.data,
	};
};

/**
 * CU-14ygtkj65jv (HI_005_005808 / HI_010_005808 / HI_015_005808) — is this
 * generic user payload writing a PATIENT demographic record?
 *
 * These endpoints are shared by patients, practitioners, admins and other user
 * types, and the server resolves "is the target a patient?" differently per
 * endpoint, so the client-side test must mirror each endpoint exactly:
 *
 *  - UPDATE (`UserController::updateUser()` / `update($id)`) resolves the type
 *    from the PERSISTED target (the authenticated user / the loaded row), never
 *    from the payload. Normalisation is selected only when the payload — built
 *    from that loaded record by every real caller — identifies the target as
 *    `user_type === 'patient'`. An absent or null `user_type` is NOT a patient.
 *  - CREATE (`UserController::store()`) defaults an ABSENT `user_type` key to
 *    'patient' via `$request->input('user_type', 'patient')`, so a payload with
 *    no key at all is a patient create. An explicitly supplied null is NOT
 *    defaulted by the server (`data_get()` substitutes the default only for a
 *    MISSING key) — it creates a `user_type = null` record under the generic
 *    `date` rule — so null is not a patient here either. Key PRESENCE, not
 *    truthiness, is what separates `{}` from `{ user_type: null }`.
 */
const isPatientRecord = (userData: Partial<User>): boolean =>
	userData.user_type === 'patient';

const isPatientCreatePayload = (userData: Partial<User>): boolean =>
	isPatientRecord(userData) || !Object.prototype.hasOwnProperty.call(userData, 'user_type');

/**
 * CU-14ygtkj65jv — canonicalise a PATIENT payload's `date_of_birth` to
 * `YYYY-MM-DD` through the shared full-DOB contract before it leaves the client.
 *
 * Two things this fixes on the generic routes, both of which the canonical
 * `PatientApi` routes have never had:
 *
 *  - a partial or non-four-digit-year representation reached the server, whose
 *    generic `date` rule accepted it and let the framework invent the missing
 *    parts;
 *  - a `Date` held in component state serialised to a UTC instant, so a local
 *    date east of UTC arrived as the PREVIOUS day. The shared normaliser reads
 *    local calendar components, so the selected day is the day that is sent.
 *
 * A non-patient payload is returned untouched. An omitted key stays omitted and
 * an explicit null stays null; an invalid value throws rather than being
 * repaired into a different date.
 */
const withPatientDob = <T extends Partial<User>>(userData: T, isPatientTarget: boolean): T =>
	isPatientTarget
		? withNormalisedDateOfBirth(userData as T & { date_of_birth?: unknown })
		: userData;

export const updateUser = async (userId: UserUuid, userData: Partial<User>) => {
	const response = await http.put(`${API_BASE_URL}/admin/users/${userId}`, withPatientDob(userData, isPatientRecord(userData)));
	return response.data;
};

/** CU-14ygtkj7wuy — the narrow server-authoritative account state returned by the one-way promotion. */
export interface PromotedPatientAccount {
	id: UserUuid;
	user_type: string;
	is_customer: boolean;
	customer_advanced: boolean;
}

/** CU-14ygtkj7wuy — the bounded Customer -> Patient promotion of the SAME user record; there is no reverse call. */
export const promoteUserToPatient = async (userId: UserUuid): Promise<PromotedPatientAccount> => {
	const response = await http.post(`${API_BASE_URL}/admin/users/${userId}/promote-to-patient`, {});
	return response.data.data;
};

export const activateUser = async (userId: UserUuid) => {
	const response = await http.put(`${API_BASE_URL}/admin/users/${userId}/activate`, {});
	return response.data;
};

export const deactivateUser = async (userId: UserUuid) => {
	const response = await http.delete(`${API_BASE_URL}/admin/users/${userId}`);
	return response.data;
};


export const createUser = async (userData: Partial<User>) => {
	const response = await http.post(`${API_BASE_URL}/admin/users`, withPatientDob(userData, isPatientCreatePayload(userData)));
	return response.data;
};

// healthcare validates both as strings until it stores them decoded (R07-L02), so an object echoed from /me goes back as JSON.
const withPreferencesAsJson = (userData: User): User => {
	const payload = { ...userData };
	if (payload.preferences !== null && typeof payload.preferences === "object") payload.preferences = JSON.stringify(payload.preferences);
	if (payload.xoe_preferences !== null && typeof payload.xoe_preferences === "object") payload.xoe_preferences = JSON.stringify(payload.xoe_preferences);
	return payload;
};

//Profile management routes for a user
export const updateProfile = async (userData: User) => {
	const response = await http.put(`${API_BASE_URL}/user`, withPatientDob(withPreferencesAsJson(userData), isPatientRecord(userData)));

	if (response.status === 200) {
		const updatedUser = response.data;

		// Merged so a response without the loaded relations does not drop them from the session.
		updateSessionUser(updatedUser);

		// Return the full user data (including preferences) for further processing
		return updatedUser;
	}

	return response.data;
};


export interface PasswordResetLink {
	uuid: UserUuid;
	sent: boolean;
	expires_at: string;
}

// Staff no longer choose a password for someone: identity emails the user a reset link (contract §15 row 63).
export const sendPasswordResetLink = async (id: UserUuid): Promise<PasswordResetLink> => {
	const response = await http.put(`${API_BASE_URL}/admin/user/passwordReset/${id}`, {});
	return response.data.data;
};



export const uploadProfilePicture = async (file: File) => {
	const formData = new FormData();
	formData.append("file", file);

	const response = await http.post(`${API_BASE_URL}/user/profile-picture`, formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
	return response.data;
};

export const deleteProfilePicture = async () => {
	const response = await http.delete(`${API_BASE_URL}/user/profile-picture`);
	return response.data;
};

